import { Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { Header, expandIcon } from 'datastake-daf/dist/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useStore from '../context';
import Style from '../../ViewTIF/style';
import Settings from '../components/Settings';
import Loading from '../../../components/Loading';
import DafTable from '../../../components/Table/newTable';
import DataStoreService from '../../../services/DataStore';
import { getLinkedColumns, getStandardColumns } from './config';
import { renderBreadCrumbs } from '../../../helpers/breadCrumbs';
import { renderRows } from '../../Analysis/sca-dds-kyc-widgets/table-components/helper';
import { getType } from '../helper';
import DataStore from '../../../services/DataStore';

export default function DataStoreView({ mod }) {
  const goTo = useNavigate();
  const { t } = useTranslation();
  const loadingKeys = useRef({});
  const [expanded, setExpanded] = useState([]);
  const [toggledKeys, setToggledKeys] = useState([]);
  const { id, namespace, group: selectedGroup } = useParams();
  const [settingsVisible, setSettingsVisible] = useState(false);
  const { data: dataStoreData, fetchForm, formLoading } = useStore();
  const [dataLoading, setDataLoading] = useState(true);
  const [dataSource, setDataSource] = useState({});

  const loading = useMemo(() => dataLoading || formLoading, [dataLoading, formLoading]);

  const form = useMemo(() => (dataStoreData?.form || {})[namespace], [namespace, dataStoreData]);

  const linkedDataColumns = useMemo(() => getLinkedColumns({ t, forms: dataStoreData?.form }), [t, dataStoreData]);
  const standardDataColumns = useMemo(() => getStandardColumns({ t, setSettingsVisible, form }), [t, form]);

  const data = useMemo(() => {
    return {
      ...(dataSource || {}),
      linkedData: (dataSource?.links || []).map((it, i) => ({ ...it, key: `${it.id}-${i}-link` })),
      standardData: (dataSource?.datapoints || []).map((it, i) => ({ ...it, key: `${it.id}-${i}-standard` }))
    }
  }, [dataSource]);

  const channel = useMemo(() => (data.channels || [])[0], [data]);

  const fetchData = async () => {
    setDataLoading(true);

    try {
      const type = getType(namespace);
      const { data } = await DataStoreService.getSubject(type, id);
      const { administrativeLevel1, administrativeLevel2, ...rest } = data?.data[0] || {};
      const levels = [administrativeLevel1, administrativeLevel2].filter((p) => !!p);
      let levelsRes = {data: []};
      
      if (levels.length) {
        levelsRes = await DataStore.getOptions({
          entity: 'AdministrativeLevel',
          filters: {ids: levels}
        });
      }

      const _dataSource = {
        ...rest
      }

      Object.assign(_dataSource, { datapoints: _dataSource.datapoints.map(dp => {
        if (dp.dataId === 'administrativeLevel1') {
          dp.data = levelsRes.data.find((l) => l.id === administrativeLevel1)?.name;
        }
        if (dp.dataId === 'administrativeLevel2') {
          dp.data = levelsRes.data.find((l) =>  l.id === administrativeLevel2)?.name;
        }
        return dp;
      }) });

      setDataSource(_dataSource);
    } catch (err) {
      console.log(err);
    }

    setDataLoading(false);
  };

  useEffect(() => {
    fetchForm();
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedGroup) {
      goTo(`/app/${mod}/data-store/${namespace}/view/${id}/standard-data`);
    }
  }, [selectedGroup]);

  const breadCrumbs = useMemo(() => {
    const _breadcrumbs = renderBreadCrumbs({ t, view: namespace, isDataStore: true, goTo, mod });
    return [..._breadcrumbs, { label: id }]
  }, [t, goTo, namespace, id, mod]);

  const toggle = useCallback((key) => setExpanded((prev) => [...prev, key]), []);
  const unToggle = useCallback((key) => setExpanded((prev) => [...prev.filter((p) => p !== key)]), []);

  const toggleExpand = useCallback((expanded, { key }) =>
    expanded ? toggle(key) : unToggle(key), [toggle, unToggle]);

  const onFinish = async (formValue) => {
    setDataLoading(true);

    try {
      await DataStoreService.saveSubjectSettings(channel?.id, formValue);
      await fetchData();
    } catch (err) {
      console.log(err);
    }

    setDataLoading(false);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Style>
      <Header
        title={data?.name || ''}
        breadcrumbs={breadCrumbs}
        tags={[{ label: id }]}
        actionButtons={[
          {
            onClick: () => { },
            disabled: true,
            icon: <DownloadOutlined />,
          },
        ]}
      />

      <div className="view-content">
        <Menu
          mode="inline"
          defaultSelectedKeys={[selectedGroup]}
          selectedKeys={[selectedGroup]}
          defaultOpenKeys={selectedGroup ? [selectedGroup.split('/')[0]] : []}
          style={{ overflowY: 'auto', overflowX: 'hidden' }}
          onSelect={g => {
            goTo(`/app/${mod}/data-store/${namespace}/view/${id}/${g.key}`);
          }}>
          <Menu.Item key="standard-data">{t('Standard Data')}</Menu.Item>
          <Menu.Item key="linked-data">{t('Linked Data')}</Menu.Item>
        </Menu>

        <div className="content">
          <div className="daf-table-wrapper no-pagination">
            {selectedGroup === 'standard-data' ? (
              <DafTable
                scrollX={false}
                columns={standardDataColumns}
                className="no-padding no-margin"
                data={data.standardData}
                loading={loading}
                pagination={false}
                rowKey="key"
                expandable={{
                  onExpand: toggleExpand,
                  rowExpandable: (a) => {
                    return Array.isArray(a.versions);
                  },
                  expandedRowRender: (d) => renderRows({
                    data: d,
                    columns: standardDataColumns,
                    expanded: toggledKeys,
                  }),
                  expandedRowKeys: expanded,
                  expandedRowClassName: () => 'expanded-table-cell'
                }}
                expandIcon={(props) => expandIcon({
                  ...props,
                  arrows: true,
                  toggledKeys,
                  setToggledKeys,
                  loadingKeys,
                })}
              />
            ) : selectedGroup === 'linked-data' ? (
              <DafTable
                scrollX={false}
                columns={linkedDataColumns}
                className="no-padding no-margin"
                data={data.linkedData}
                loading={loading}
                pagination={false}
                rowKey="key"
              />
            ) : null}
          </div>
        </div>
      </div>

      {!!channel && (
        <Settings
          defaultData={{
            discoverability: channel.discoverability || 'public',
            accessibility: channel.accessibility || 'uponAcceptance',
            history: channel.history || 'all',
            stream: channel.stream || 'permanent',
            delivery: channel.delivery || 'custom',
            licensing: channel.licensing || 'citation',
          }}
          onFinish={onFinish}
          title="Datapoint Settings"
          settingsVisible={settingsVisible}
          setSettingsVisible={setSettingsVisible}
        />
      )}
    </Style>
  );
}
